import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Users, AlertTriangle, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/shared/empty-state";
import { StatCard } from "@/features/dashboard/stat-card";
import { TeamCompletionChart } from "@/features/dashboard/team-completion-chart";
import { AtRiskList } from "@/features/dashboard/at-risk-list";
import { useTeamDashboard } from "@/hooks/use-dashboard";
import { useCurrentUser, isSupervisorOrAbove } from "@/hooks/use-current-user";
import { getQuarterOptions } from "@/lib/fiscal-year";
import { YEAR_OPTIONS, DEFAULT_YEAR } from "@/lib/year-options";
import type { Quarter } from "@/types/entities";

function TeamDashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-56" />
      <div className="grid gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="flex items-center gap-4 p-6">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-7 w-12" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-44" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

export function TeamDashboardPage() {
  const [year, setYear] = useState(DEFAULT_YEAR);
  const [quarter, setQuarter] = useState<string>("all");
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();

  const quarterParam = quarter === "all" ? undefined : (quarter as Quarter);
  const { data, isLoading, error } = useTeamDashboard(year, quarterParam);
  const summary = data?.data;

  // Redirect non-supervisor/non-admin users
  if (currentUser && !isSupervisorOrAbove(currentUser.role)) {
    void navigate({ to: "/dashboard" });
    return null;
  }

  if (isLoading) return <TeamDashboardSkeleton />;

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="font-heading text-3xl font-semibold">Team Dashboard</h1>
        <EmptyState
          title="Failed to load team dashboard"
          description={error.message}
        />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="space-y-6">
        <h1 className="font-heading text-3xl font-semibold">Team Dashboard</h1>
        <EmptyState
          title="No team data"
          description="Your team members' objectives and key results will appear here."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-heading text-2xl font-semibold sm:text-3xl">Team Dashboard</h1>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={quarter} onValueChange={setQuarter}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[{ value: "all", label: "All Quarters" }, ...getQuarterOptions(year)].map((q) => (
                <SelectItem key={q.value} value={q.value}>
                  {q.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-full sm:w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEAR_OPTIONS.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          title="Team Members"
          value={summary.team_completion.length}
          icon={<Users className="h-6 w-6 text-[hsl(var(--primary))]" />}
        />
        <StatCard
          title="At Risk"
          value={summary.at_risk_key_results.length}
          icon={<AlertTriangle className="h-6 w-6 text-[hsl(var(--destructive))]" />}
          description={summary.at_risk_key_results.length > 0 ? "Behind expected pace" : "All on track"}
        />
        <StatCard
          title="Stalled"
          value={summary.stalled_key_results.length}
          icon={<Clock className="h-6 w-6 text-[hsl(var(--accent))]" />}
          description={summary.stalled_key_results.length > 0 ? "No recent updates" : "All active"}
        />
      </div>

      {/* Team Completion Chart */}
      <TeamCompletionChart data={summary.team_completion} />

      {/* At-Risk Key Results */}
      <AtRiskList
        title="At-Risk Key Results"
        keyResults={summary.at_risk_key_results}
        emptyMessage="No key results behind pace."
      />

      {/* Stalled Key Results */}
      <AtRiskList
        title="Stalled Key Results"
        keyResults={summary.stalled_key_results}
        emptyMessage="All key results have recent activity."
      />
    </div>
  );
}
